import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Megaphone } from 'lucide-react';
import type { AvvisoDiServizio as Avviso } from '@draughts/shared';
import { normalizeLanguage } from '../../i18n';
import { useAvvisoDiServizio } from '../../hooks/useAvvisoDiServizio';
import './AvvisoDiServizio.css';

/**
 * «Riavvio programmato alle 23:00» — la striscia di servizio della home.
 *
 * Prima di questo avviso non esisteva NESSUN modo di dire qualcosa a chi usa
 * l'app: un riavvio per un deploy arrivava addosso senza preavviso, e il droplet
 * di prova — che serve la stessa build della produzione — non si distingueva da
 * quello vero nemmeno a guardarlo.
 *
 * Non si richiude, come l'avviso «senza rete» qui accanto: un pulsante di
 * chiusura vorrebbe dire tenere uno stato «l'ho gia' letto» e decidere quando
 * riarmarlo, cioe' lavoro vero per togliere una riga che dura quanto la cosa che
 * descrive — e che la descrive proprio a chi sta per esserne colpito. Il suo
 * modo di sparire e' la `scadenza` nel file, che il server fa valere da solo.
 *
 * Il testo puo' arrivare in due forme (vedi `avviso-servizio.ts`): un `preset`,
 * tradotto qui in tutte e otto le lingue, oppure il testo libero scritto
 * nell'avviso. Il secondo e' l'UNICO punto dell'app in cui si stampa una stringa
 * arbitraria che viene dal server: lo si fa come figlio JSX, cosi' React la
 * scherma, e mai attraverso `t()`.
 */
export default function AvvisoDiServizio() {
  const { t, i18n } = useTranslation();
  const avviso = useAvvisoDiServizio();

  if (!avviso) return null;

  const lingua = normalizeLanguage(i18n.language);
  const corpo = avviso.preset
    ? testoPreset(t, avviso.preset, oraLocale(avviso.instante, lingua))
    : // `en` e' garantito dal server quando `messaggi` non e' nullo: e' il
      // ripiego di chi legge in una lingua che il file non porta.
      (avviso.messaggi?.[lingua] ?? avviso.messaggi?.en ?? null);

  if (!corpo) return null;

  return (
    <div
      className={`avviso-di-servizio avviso-di-servizio--${avviso.livello}`}
      role="status"
      data-testid="avviso-di-servizio"
    >
      <span className="avs-icona" aria-hidden="true"><Megaphone size={18} /></span>
      <span className="avs-testo">
        <span className="avs-titolo">{t('avvisoServizio.etichetta', 'Service notice')}</span>
        <span className="avs-corpo">{corpo}</span>
      </span>
    </div>
  );
}

/**
 * L'istante nel fuso di CHI LEGGE, non in quello di chi ha scritto l'avviso.
 *
 * Con otto lingue e un pubblico sparso l'ora va formattata sul posto; e la data
 * compare quando l'istante non cade oggi, perche' «riavvio alle 23:00» quando in
 * realta' e' domani sera e' una bugia — e annunciare con un giorno di anticipo
 * e' il caso normale, non l'eccezione.
 *
 * Se l'istante e' impresentabile si torna `null` e il chiamante usa la frase
 * senza orario: inerte anche qui.
 */
function oraLocale(instante: string | null, lingua: string): string | null {
  if (!instante) return null;
  const quando = new Date(instante);
  if (Number.isNaN(quando.getTime())) return null;

  const adesso = new Date();
  const stessoGiorno =
    quando.getFullYear() === adesso.getFullYear() &&
    quando.getMonth() === adesso.getMonth() &&
    quando.getDate() === adesso.getDate();

  return new Intl.DateTimeFormat(
    lingua,
    stessoGiorno
      ? { hour: '2-digit', minute: '2-digit' }
      : { dateStyle: 'short', timeStyle: 'short' },
  ).format(quando);
}

/**
 * Le chiavi sono LETTERALI dentro `t()`, e lo `switch` e' esaustivo.
 *
 * Non una mappa `Record<PresetAvviso, …>`: la rete che verifica che una chiave
 * invocata esista davvero (`i18n-chiavi-invocate-esistenti.test.ts`) legge il
 * sorgente e riconosce solo `t('chiave.letterale')`. Con una mappa queste cinque
 * chiavi le sarebbero invisibili, e un refuso stamperebbe la chiave grezza in
 * otto lingue. Cosi' le copre gratis, e TypeScript controlla che non manchi un
 * preset.
 */
function testoPreset(
  t: TFunction,
  preset: NonNullable<Avviso['preset']>,
  ora: string | null,
): string {
  switch (preset) {
    case 'riavvio':
      return ora
        ? t('avvisoServizio.preset.riavvioAlle', { ora })
        : t('avvisoServizio.preset.riavvio');
    case 'manutenzione':
      return ora
        ? t('avvisoServizio.preset.manutenzioneAlle', { ora })
        : t('avvisoServizio.preset.manutenzione');
    case 'rallentamenti':
      return t('avvisoServizio.preset.rallentamenti');
  }
}
